<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRandomUUID } from "~/composables/useRandomUUID";
import { twMerge } from "tailwind-merge";

const randomId = ref("");

// Generating random ID
onMounted(() => {
  randomId.value = useRandomUUID();
});

// Variants Types
type Sizes = "sm" | "base" | "lg";

// Props
const { size, readOnly, modelValue, disabled, dataTestid } = defineProps({
  label: { type: String, default: null },
  size: { type: String as PropType<Sizes>, default: "base" },
  stars: { type: Number, default: 5 },
  readOnly: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  appendClass: { type: String, default: "" },
  modelValue: { type: Number, default: 0 },
  dataTestid: { type: String, default: "molecule-ratings" },
});

const updateValue = (value: number) => {
  if (readOnly || disabled) {
    return modelValue;
  }
  return value;
};

function getStarWidth(index: number): string {
  const wholeStars = Math.floor(modelValue);
  // decimal part
  let fraction = (modelValue - wholeStars) * 100;

  if (index < wholeStars) return "100%";
  // Buffing it by 5% for noticeable visibility
  if (index === wholeStars)
    return `${fraction < 50 ? fraction + 5 : fraction}%`;
  return "0%";
}

const starSizeClass = computed(() => {
  return {
    base: "w-6 h-6",
    sm: "w-5 h-5",
    lg: "w-10 h-10",
  }[size];
});
</script>

<template>
  <div :class="twMerge('inline-flex items-center', appendClass)" :id="randomId">
    <div
      v-for="(i, index) in stars"
      :key="i"
      @click="$emit('update:modelValue', updateValue(i))"
      :class="[disabled || readOnly ? '' : 'cursor-pointer']"
      :data-testid="dataTestid + '-stars'"
    >
    <svg
        :data-testid="dataTestid"
        :class="starSizeClass"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            :id="`star-fill-${index}-${randomId}`"
            x1="0"
            x2="1"
            y1="0"
            y2="0"
          >
            <stop offset="0%" stop-color="#F1C40F" />
            <stop :offset="getStarWidth(index)" stop-color="#F1C40F" />
            <stop :offset="getStarWidth(index)" stop-color="#D9D9D9" />
          </linearGradient>
        </defs>
        <path
          d="M12 2L14.66 8.34L21.52 9.27L16.76 13.97L18.04 20.76L12 17.77L5.96 20.76L7.24 13.97L2.48 9.27L9.34 8.34L12 2Z"
          :fill="`url(#star-fill-${index}-${randomId})`"
        />
      </svg>
    </div>
    <slot name="label">
      <div
        v-if="label"
        :data-testid="dataTestid"
        class="text-natural-grey ml-1 pt-[3px]"
      >
        {{ label }}
      </div>
    </slot>
  </div>
</template>
